// Service Desk metrics dashboard: volume over time, response/resolution speed,
// breakdowns by brand/category/priority, open vs closed and SLA compliance.
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  getMetrics, getSettings, getServiceDeskAgents, formatMins,
  type Metrics, type ServiceDeskSettings, type AgentsPanel,
} from "../services/serviceDeskService";
import { DeskLoadErrorPanel, useDeskLoadError } from "../components/DeskLoadError";
import { AgentQueueWarning } from "./AgentsSection";

const COLORS = ["#3B82F6", "#8B5CF6", "#10B981", "#F59E0B", "#EF4444", "#06B6D4", "#EC4899"];

/**
 * Aviso de muestras descartadas de los promedios (#410).
 *
 * El backend ya no suma tramos invertidos (`resolvedAt` anterior a `createdAt`,
 * típico de tickets importados o relojes desfasados), así que la media dejó de
 * salir negativa. Pero callarlo cambia un número visiblemente absurdo por uno
 * silenciosamente incompleto: si se descartan 400 muestras, la mesa tiene que
 * enterarse de que hay datos que arreglar. Devuelve null cuando no hay nada que
 * decir, para no meter ruido en el caso normal.
 */
export function skewedSamplesNotice(skewedSamples?: number | null): string | null {
  if (typeof skewedSamples !== "number" || !Number.isFinite(skewedSamples) || skewedSamples <= 0) {
    return null;
  }
  const plural = skewedSamples === 1 ? "muestra" : "muestras";
  return `${skewedSamples} ${plural} con fechas incoherentes (el fin antes del inicio) quedaron fuera de los promedios. Revisa esos tickets: suelen venir de una importación o de un reloj desfasado.`;
}

export default function DashboardSection() {
  const { t } = useTranslation("serviceDesk");
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [settings, setSettings] = useState<ServiceDeskSettings | null>(null);
  const [brand, setBrand] = useState("");
  const [loading, setLoading] = useState(true);
  // Sin esto, un 403 de agente dejaba el dashboard en "Cargando métricas…" para
  // siempre: `metrics` seguía en null y nadie capturaba el rechazo.
  const { error: loadError, capture, reset } = useDeskLoadError();

  // #511: el aviso de "hay cola de código y ningún agente en línea" vive aquí
  // además de en la sección Agentes — es el tablero que el admin mira primero.
  // Un fallo (p. ej. sin permiso) no rompe el dashboard: simplemente no avisa.
  const [agentsPanel, setAgentsPanel] = useState<AgentsPanel | null>(null);

  useEffect(() => { getSettings().then(setSettings).catch(() => {}); }, []);
  useEffect(() => { getServiceDeskAgents().then(setAgentsPanel).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    reset();
    getMetrics(brand ? { brand } : {}).then(setMetrics).catch(capture).finally(() => setLoading(false));
  }, [brand, capture, reset]);

  const categoryLabel = useMemo(() => {
    const map: Record<string, string> = {};
    (settings?.categories || []).forEach((c) => { map[c.id] = c.label; });
    return map;
  }, [settings]);
  const labelLabel = useMemo(() => {
    const map: Record<string, string> = {};
    (settings?.labels || []).forEach((l) => { map[l.id] = l.label; });
    return map;
  }, [settings]);

  const card = "bg-surface border-line";
  const text = "text-content";
  const muted = "text-content-muted";

  // Guard the whole `totals` sub-object, not just `metrics` — a partial/legacy
  // payload (truthy but without `totals`) must not crash the dashboard.
  if (loadError) return <DeskLoadErrorPanel error={loadError} />;
  if (loading || !metrics?.totals) return <div className="p-8 text-center text-gray-400">{t("dashboard.loading")}</div>;

  // Defensive defaults so a missing series never throws inside recharts.
  const volumeByDay = metrics.volumeByDay || [];
  const byBrand = metrics.byBrand || [];
  const byCategory = metrics.byCategory || [];
  const byPriority = metrics.byPriority || [];
  const byLabel = metrics.byLabel || [];

  const kpis = [
    { label: t("dashboard.kpis.totalTickets"), value: metrics.totals.all },
    { label: t("dashboard.kpis.open"), value: metrics.totals.open },
    { label: t("dashboard.kpis.closed"), value: metrics.totals.closed },
    { label: t("dashboard.kpis.avgFirstResponse"), value: formatMins(metrics.avgFirstResponseMins) },
    { label: t("dashboard.kpis.avgResolution"), value: formatMins(metrics.avgResolutionMins) },
    { label: t("dashboard.kpis.slaCompliance"), value: metrics.slaCompliancePct != null ? `${metrics.slaCompliancePct}%` : "—" },
  ];

  const skewedNotice = skewedSamplesNotice(metrics.skewedSamples);

  // Requester-driven closure (CC-CONSOLE #1337). Their own row: they are not
  // team performance (the ones above) but how long the customer takes, and
  // mixed in they would read as an SLA the desk is breaching.
  const closureKpis: { label: string; value: string | number; hint?: string }[] = [
    { label: t("dashboard.kpis.avgWaitingCustomer"), value: formatMins(metrics.avgWaitingCustomerMins ?? null) },
    { label: t("dashboard.kpis.avgRequesterClosure"), value: formatMins(metrics.avgRequesterClosureMins ?? null) },
    {
      label: t("dashboard.kpis.pendingClosure"),
      value: metrics.pendingClosure ?? "—",
      hint: metrics.pendingClosure != null
        ? t("dashboard.kpis.pendingClosureUnlocked", { n: metrics.pendingClosureUnlocked ?? 0 })
        : undefined,
    },
    {
      label: t("dashboard.kpis.reworkedTickets"),
      value: metrics.reworkedTickets ?? "—",
      hint: metrics.reworkedTickets != null
        ? t("dashboard.kpis.reworkTotal", { n: metrics.reworkTotal ?? 0 })
        : undefined,
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className={`text-xl font-bold ${text}`}>{t("dashboard.title")}</h1>
        <select
          className={`rounded-lg border px-3 py-1.5 text-sm !bg-app !border-line !text-content`}
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
        >
          <option value="">{t("dashboard.allBrands")}</option>
          {(settings?.brands || []).map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
        </select>
      </div>

      <AgentQueueWarning warning={agentsPanel?.warning || null} queue={agentsPanel?.queue} />

      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map((k) => (
          <div key={k.label} className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs ${muted}`}>{k.label}</p>
            <p className={`mt-1 text-2xl font-bold ${text}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {skewedNotice && (
        <div
          role="status"
          className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200"
        >
          {skewedNotice}
        </div>
      )}

      <h2 className={`mb-2 text-sm font-semibold ${text}`}>{t("dashboard.closureTitle")}</h2>
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {closureKpis.map((k) => (
          <div key={k.label} className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs ${muted}`}>{k.label}</p>
            <p className={`mt-1 text-2xl font-bold ${text}`}>{k.value}</p>
            {k.hint && <p className={`mt-0.5 text-xs ${muted}`}>{k.hint}</p>}
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title={t("dashboard.charts.volumeOverTime")} card={card} text={text}>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={volumeByDay}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-line" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} dot={false} name="Tickets" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("dashboard.charts.byBrand")} card={card} text={text}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byBrand}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-line" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#8B5CF6" name="Tickets" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("dashboard.charts.byCategory")} card={card} text={text}>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={byCategory.map((c) => ({ name: categoryLabel[c.key] || c.key, value: c.count }))}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                label
              >
                {byCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("dashboard.charts.byPriorityLabel")} card={card} text={text}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={[
              ...byPriority.map((p) => ({ name: t(`ticketDetail.management.priorities.${p.key}`, p.key), count: p.count })),
              ...byLabel.map((l) => ({ name: labelLabel[l.key] || l.key, count: l.count })),
            ]}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-line" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#10B981" name="Tickets" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function ChartCard({ title, card, text, children }: { title: string; card: string; text: string; children: React.ReactNode }) {
  return (
    <div className={`rounded-xl border p-4 ${card}`}>
      <h2 className={`mb-3 text-sm font-semibold ${text}`}>{title}</h2>
      {children}
    </div>
  );
}
